import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Typography,
  Row,
  Col,
  Divider,
  Descriptions,
  Button,
  Spin,
  Dropdown,
  Menu,
  Icon,
} from 'antd';
import {
  propertySearchDetailsClear,
  exportPropertyDetailsAsync,
  searchXosDocument,
} from './PropertySearchDetails.actions';
import {
  BasicDrawer,
  AttributeValuePair,
  formatLongDate,
  TableSizeNames,
  CommonTable,
  IconTypeNames,
  Sizes,
  LOADING,
  formatSize,
  PrintButton,
} from 'common';
import { FullPropertyDetails, PolicyInformation } from '../models';
import { AppState } from 'store';
import ButtonGroup from 'antd/lib/button/button-group';
import { formatDate, getAccessToken } from 'common/helpers';
import Column from 'antd/lib/table/Column';
import { useAccount, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';

const { Text } = Typography;

const PropertySearchDetails: React.FC<{
  searchId: number;
  data: FullPropertyDetails;
}> = (props: { searchId: number; data: FullPropertyDetails }) => {
  const dispatch = useDispatch();
  const { county, owners } = props.data.parent;

  const { loading }: any = useSelector((state: AppState) => ({
    loading: state.propertySearchDetails.loading,
  }));

  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || {});

  const {
    taxRollYear,
    propertyInformation,
    propertyIdentifier,
    buildingInformation,
    description,
    policyInformation,
  } = props.data.details;
  const {
    acres,
    squareFootage,
    land,
    lotSize,
    total,
    zoning,
    yearBuilt,
    lotGroup,
    structure,
  } = description;
  const { itemNumber, schoolCode, swissCode, city, address } =
    propertyInformation;
  const {
    section,
    subSection,
    block,
    lot,
    subLot,
    printKey,
    suffix,
    district,
    subBlock,
    unitNumber,
    buildingNumber,
    volume,
    filedMapInformation,
  } = propertyIdentifier;

  const handleSearch = (company: string, docDesc: string, county: string) => {
    if (account && inProgress === InteractionStatus.None) {
      getAccessToken(instance, account, inProgress).then((response) => {
        if (response?.accessToken)
          dispatch(
            searchXosDocument(company, docDesc, county, response.accessToken),
          );
      });
    }
  };

  const handleDetailsExport = React.useCallback(
    (format: string, action: 'print' | 'save') => {
      if (account && inProgress === InteractionStatus.None) {
        getAccessToken(instance, account, inProgress).then((response) => {
          if (response?.accessToken)
            dispatch(
              exportPropertyDetailsAsync(
                props.data,
                props.searchId,
                format,
                action,
                response.accessToken,
              ),
            );
        });
      }
    },
    [dispatch, instance, account, inProgress, props.data, props.searchId],
  );

  return (
    <BasicDrawer
      title={'Property Details'}
      onBack={() => dispatch(propertySearchDetailsClear())}
    >
      <Spin spinning={loading} size={Sizes.LARGE} tip={LOADING}>
        <Row type="flex" justify="space-between">
          <Col>
            <AttributeValuePair
              attribute={`Tax Roll Year`}
              value={taxRollYear === 0 ? '' : taxRollYear}
            />
          </Col>
          <Col>
            <ButtonGroup>
              <PrintButton
                size={TableSizeNames.SMALL}
                icon={IconTypeNames.PRINTER}
                onClick={() => handleDetailsExport('html', 'print')}
              />
              <Dropdown
                overlay={
                  <Menu onClick={({ key }) => handleDetailsExport(key, 'save')}>
                    <Menu.Item key="doc">
                      <Icon type="file-word" />
                      MS Word
                    </Menu.Item>
                    <Menu.Item key="pdf">
                      <Icon type="file-pdf" />
                      PDF
                    </Menu.Item>
                    <Menu.Item key="html">
                      <Icon type="html5" />
                      HTML
                    </Menu.Item>
                    <Menu.Item key="txt">
                      <Icon type="file-text" />
                      Text
                    </Menu.Item>
                  </Menu>
                }
              >
                <Button
                  type="primary"
                  size={TableSizeNames.SMALL}
                  shape="round"
                  icon="download"
                >
                  Export&nbsp;
                  <Icon type="down" />
                </Button>
              </Dropdown>
            </ButtonGroup>
          </Col>
        </Row>
        <Divider dashed />
        <Descriptions
          title="Property Information"
          bordered
          size={'small'}
          column={3}
        >
          <Descriptions.Item label="County" span={1}>
            {county}
          </Descriptions.Item>
          <Descriptions.Item label="Town/City/Village" span={1}>
            {city}
          </Descriptions.Item>
          <Descriptions.Item label="Address" span={1}>
            {address}
          </Descriptions.Item>
          <Descriptions.Item label="Item Number" span={1}>
            {itemNumber}
          </Descriptions.Item>
          <Descriptions.Item label="School Code" span={2}>
            {schoolCode}
          </Descriptions.Item>
          <Descriptions.Item label="Swis Code">{swissCode}</Descriptions.Item>
        </Descriptions>

        <Descriptions
          title="Property Owner Name Information"
          bordered
          size={'small'}
        >
          {owners.map((item, index) => (
            <Descriptions.Item key={index} label="Owner">
              {item.name}
            </Descriptions.Item>
          ))}
        </Descriptions>

        <Descriptions
          title="Property Identifier"
          bordered
          size={'small'}
          column={3}
        >
          <Descriptions.Item label="District" span={1}>
            {district}
          </Descriptions.Item>
          <Descriptions.Item label="Section" span={1}>
            {section}
          </Descriptions.Item>
          <Descriptions.Item label="Sub Section">
            {subSection}
          </Descriptions.Item>
          <Descriptions.Item label="Block" span={1}>
            {block}
          </Descriptions.Item>
          <Descriptions.Item label="SubBlock" span={1}>
            {subBlock}
          </Descriptions.Item>
          <Descriptions.Item label="Print Key">{printKey}</Descriptions.Item>
          <Descriptions.Item label="Lot">{lot}</Descriptions.Item>
          <Descriptions.Item label="Sub Lot">{subLot}</Descriptions.Item>
          <Descriptions.Item label="Suffix">{suffix}</Descriptions.Item>
          <Descriptions.Item label="Building No.">
            {buildingNumber}
          </Descriptions.Item>
          <Descriptions.Item label="Unit No.">{unitNumber}</Descriptions.Item>
          <Descriptions.Item label="Volume">{volume}</Descriptions.Item>
          <Descriptions.Item label="Filed Map Information" span={3}>
            {filedMapInformation}
          </Descriptions.Item>
        </Descriptions>

        {buildingInformation && (
          <Descriptions
            title="Property Building Information"
            bordered
            size={'small'}
          >
            <Descriptions.Item label="Description">
              {buildingInformation.description}
            </Descriptions.Item>
          </Descriptions>
        )}

        {description && (
          <Descriptions
            title="Property Description"
            bordered
            size={'small'}
            column={3}
          >
            <Descriptions.Item label={'Acreage/Square Footage'}>
              {`${acres === 0 ? '' : acres}${
                acres !== 0 && squareFootage !== 0 ? '/' : ''
              }${squareFootage === 0 ? '' : squareFootage}`}
            </Descriptions.Item>
            <Descriptions.Item label="Lot Size" span={1}>
              {lotSize === '0.00X0.00' ? 'NA' : lotSize}
            </Descriptions.Item>
            <Descriptions.Item label="Zoning" span={1}>
              {zoning === '' ? 'NA' : zoning}
            </Descriptions.Item>
            <Descriptions.Item label="Land">
              {formatSize(land)}
            </Descriptions.Item>
            <Descriptions.Item label="Total" span={1}>
              {formatSize(total)}
            </Descriptions.Item>
            <Descriptions.Item label="YearBuilt" span={1}>
              {yearBuilt}
            </Descriptions.Item>
            <Descriptions.Item label="Lot Group" span={1}>
              {lotGroup}
            </Descriptions.Item>
            <Descriptions.Item label="Structure" span={1}>
              {structure}
            </Descriptions.Item>
          </Descriptions>
        )}

        {policyInformation && policyInformation.length > 0 && (
          <Descriptions title="Policy Information" size={'small'} column={5} />
        )}

        {policyInformation && policyInformation.length > 0 && (
          <CommonTable items={policyInformation} rowKey="orderNumber">
            <Column
              width="10rem"
              title="Dated"
              dataIndex="dated"
              render={(text: string) => {
                return formatDate(text);
              }}
            />
            <Column width="8rem" title="Order Number" dataIndex="orderNumber" />
            <Column width="8rem" title="Company" dataIndex="company" />
            <Column width="20rem" title="Remarks" dataIndex="remarks" />
            <Column
              width="20rem"
              title="Click to Export"
              render={(text: string, record: PolicyInformation) => (
                <>
                  <Button
                    type="link"
                    size="small"
                    icon="download"
                    onClick={() =>
                      handleSearch(record.company, record.orderNumber, county)
                    }
                    title="Click to export policy document"
                  >
                    Export as PDF
                  </Button>
                  <p
                    style={{
                      display: 'inline',
                      color: '#40a9ff',
                      fontWeight: 'bold',
                    }}
                  >
                    *
                  </p>
                  <p style={{ display: 'inline' }}>Fee applied on Export</p>
                  <p
                    style={{
                      display: 'inline',
                      color: '#40a9ff',
                      fontWeight: 'bold',
                    }}
                  >
                    *
                  </p>
                </>
              )}
            />
          </CommonTable>
        )}

        <Divider dashed />
        <Row type="flex" justify="center">
          <Text>
            This report is intended for informational purposes only, and may not
            include assessment of other agencies including villages.
          </Text>
        </Row>
        <Row type="flex" justify="center">
          <Text>Generated: {formatLongDate(new Date())}</Text>
        </Row>
      </Spin>
    </BasicDrawer>
  );
};

export default PropertySearchDetails;
